// src/utils/netWorthMath.js
// Single source of truth for which sinking funds count toward net worth.
//
// A fund is counted unless it has `exclude_from_net_worth: true`. Net worth is
// always recalculated from transactions (past months included), so turning a
// fund off removes it from every month's figure and from the charts, and
// turning it back on restores them exactly. Nothing about the fund changes.

import { countsInProjection, signOf } from "./futureIncome"; // plan_25, plan_28

export const countsInNetWorth = (fund) => !!fund && fund.exclude_from_net_worth !== true;

// The funds that count, in their original order.
export const getNetWorthFunds = (sinkingFunds = []) =>
  (sinkingFunds || []).filter(countsInNetWorth);

// How many funds are left out (for the "X funds not counted" note).
export const countExcludedFunds = (sinkingFunds = []) =>
  (sinkingFunds || []).filter(f => f && !countsInNetWorth(f)).length;

// Planner money not yet moved into accounts, counting only funds that count
// toward net worth plus investment accounts. Same allocation rule as the
// Projected tile (and fundMath's getProjectedAmount), so the tile and the
// chart's Projected line always agree.
export const getPendingForNetWorth = ({ sinkingFunds = [], investmentAccounts = [], extraPayments = [] } = {}) => {
  const fundIds = getNetWorthFunds(sinkingFunds).map(f => f.id);
  const invIds = (investmentAccounts || []).map(i => i.id);
  return (extraPayments || [])
    .filter(p => !p.moved_to_account && countsInProjection(p))
    .reduce((sum, p) => {
      let s = 0;
      fundIds.forEach(id => {
        if (p.fund_allocations && p.fund_allocations[id]) {
          s += signOf(p) * p.fund_allocations[id];
          return;
        }
        const list = p.designated_funds || (p.designated_fund ? [p.designated_fund] : []);
        if (list.includes(id)) s += signOf(p) * (p.amount || 0) / list.length;
      });
      invIds.forEach(id => {
        if (p.investment_allocations && p.investment_allocations[id]) s += p.investment_allocations[id];
      });
      return sum + s;
    }, 0);
};
